import rtlText from "@mapbox/mapbox-gl-rtl-text";

const CHARACTER_RANGE_RTL = "\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC";
const CHARACTER_RANGE_LTR =
  "A-za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02B8\u0300-\u0590\u0800-\u1FFF\u200E\u2C00-\uFB1C\uFE00-\uFE6F\uFEFDD-\uFFFF";

// eslint-disable-next-line no-misleading-character-class
export const rtlRegExp = new RegExp(
  "^[^" + CHARACTER_RANGE_LTR + "]*[" + CHARACTER_RANGE_RTL + "]",
);

export const isStringRTL = (text: string) => {
  return rtlRegExp.test(text);
};

let rtlTextInstance: typeof rtlText | null = null;

// initialize async rtl module
export const initializeHandleRTLText = async (): Promise<void> => {
  if (!rtlTextInstance) {
    rtlTextInstance = await rtlText;
  }
};

export const handleRTLText = (text: string): string => {
  if (!rtlTextInstance) {
    throw new Error("rtlText is not initialized yet.");
  }

  const { applyArabicShaping, processBidirectionalText } = rtlTextInstance;

  // Remove newlines
  text = text.replace(/\n/g, " ");

  // Convert to RTL
  const shapedText = applyArabicShaping(text);

  // Process BiDi text
  const processedTextArray = processBidirectionalText(shapedText, []);

  // Join the array into a single string
  return processedTextArray.join("");
};

/**
 * Obtains width of rendered 2d text
 * @returns
 */

export const getTextWidth = (
  message: string,
  fontSize?: number,
  fontFamily?: string | null,
) => {
  // Create canvas element
  const tempCanvas = document.createElement("canvas");
  const tempCtx = tempCanvas.getContext("2d");
  if (!tempCtx) return 0;

  // Set font size and family
  tempCtx.font = `${fontSize}px ${fontFamily || "Arial"}`;
  const textWidth = tempCtx.measureText(message).width;
  return textWidth;
};

/**
 * Parses an associated POI id supporting both the current format and legacy format
 * caused by a previous bug where POI ids were stored as `"id - name"`.
 *
 * Legacy format:
 *   "123 - POI Name"
 *
 * Current format:
 *   "123"
 *
 * @param rawPoiId - The raw POI identifier value coming from the GeoJson.
 * It may be a legacy string (`"id - name"`), a numeric id, or a numeric string.
 *
 * @returns The parsed numeric POI id, or `null` if the value cannot be parsed.
 */
export function parseAssociatedPoiId(rawPoiId: string | number): number | null {
  let poiId: number;
  if (typeof rawPoiId === "string") {
    poiId = Number(rawPoiId.split(" - ")[0]);
  } else {
    poiId = Number(rawPoiId);
  }

  return Number.isNaN(poiId) ? null : poiId;
}